import { faCircleUser, faUserCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArchive,
    faArrowTurnLeft,
    faDownload,
    faSpinner,
} from "@fortawesome/sharp-light-svg-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type {
    EmissionEventFullRequest,
    EmissionRecordsListRequest,
} from "../../apiClient/generated";
import {
    useAdminApiClient,
    useAppSelector,
    useEmissionEventsApiClient,
    useEmissionRecordsApiClient,
} from "../../hooks";
import { ROUTES } from "../../routes";
import {
    type BulkActionsConfirmationProps,
    BulkActionsMenu,
    type BulkActionsProps,
    BulkFailuresDownloadButton,
    BulkFailuresPreview,
    BulkFailuresTruncatedNote,
    BulkMenuItem,
} from "../../ui/BulkTableAction";
import { PrimaryButton, SecondaryButton } from "../../ui/Buttons";
import { CustomCheckbox } from "../../ui/CustomCheckbox";
import { FilteringDropdown } from "../../ui/Inputs";
import { FullscreenModal, Modal } from "../../ui/Modals";
import { DataExportStatus } from "../dashboards/DataExport";
import { UserDropdown } from "../EventsV2/UserDropdown";

// Filters come from emissionTableFiltersAtom and are spread into the bulk
// endpoints, so they carry the emission list-request shape.
type EmissionBulkProps = BulkActionsProps<EmissionRecordsListRequest>;
type EmissionBulkModalProps =
    BulkActionsConfirmationProps<EmissionRecordsListRequest>;

const emissionFailureLink = (id: string) =>
    `${ROUTES.DASHBOARD_ALL}?selectedEvent=${id}`;

const BulkAssignModal = (props: EmissionBulkModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();
    const adminApiClient = useAdminApiClient();
    const isStaff = useAppSelector((state) => state.auth.isStaff);
    const currentUserCompany = useAppSelector((state) => state.auth.company);

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return apiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    // Mutation control
    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );
    const [selectedCompany, setSelectedCompany] = useState<number>(null);
    const [userId, setUserId] = useState<number>(null);

    const companiesQuery = useQuery({
        queryKey: ["staffCompanyList"],
        queryFn: async () => {
            return await adminApiClient.adminAccountsCompaniesList();
        },
        enabled: isStaff,
    });

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.emissioneventsBulkAssignCreate({
                bulkAssignRequest: { userId: String(userId) },
                ...(assignToAllFiltered
                    ? props.filters
                    : { emissionRecordIds: props.selectedIds }),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries();
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    return (
        <Modal
            visible={props.visible}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">Bulk assign</h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Select the user you want to assign{" "}
                        {affectedCount ?? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                className="animate-spin"
                            />
                        )}{" "}
                        emission
                        {affectedCount !== 1 ? "s" : ""} to:
                    </p>
                    {isStaff && (
                        <>
                            <p className="text-sm text-gray-600 mb-1 block">
                                Company
                                <sup>(admin only)</sup>
                            </p>
                            <FilteringDropdown
                                value={selectedCompany}
                                onChange={(c) => {
                                    setSelectedCompany(c as number);
                                    setUserId(null);
                                }}
                                options={
                                    companiesQuery.data?.map((c) => ({
                                        id: c.id,
                                        displayName: c.name,
                                        searchString: c.name.toLowerCase(),
                                    })) ?? []
                                }
                            />
                            <div className="mt-3" />
                        </>
                    )}
                    <UserDropdown
                        value={userId}
                        onChange={(id) => setUserId(id)}
                        company={
                            isStaff ? selectedCompany : currentUserCompany?.id
                        }
                    />
                    {props.allSelected && (
                        <div className="flex items-center gap-2 text-sm mt-4">
                            <CustomCheckbox
                                checked={assignToAllFiltered ? "yes" : "no"}
                                onClick={() =>
                                    setAssignedToAllFiltered(
                                        !assignToAllFiltered,
                                    )
                                }
                            />
                            Apply action to all filtered emissions
                        </div>
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={
                                !userId ||
                                bulkActionMutation.isPending ||
                                (allSelectedCountQuery.isLoading &&
                                    props.allSelected)
                            }
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Assign to{" "}
                            {affectedCount ?? (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    className="animate-spin"
                                />
                            )}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Assigning emissions...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully assigned {bulkActionMutation.data.ok}{" "}
                        emission{bulkActionMutation.data.ok !== 1 ? "s" : ""}.
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} emission
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be assigned. Download the CSV to see
                                the reason for each failure.
                            </p>
                            <BulkFailuresPreview
                                linkForId={emissionFailureLink}
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={emissionFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-assign"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

const BulkAssignReviewerModal = (props: EmissionBulkModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();
    const adminApiClient = useAdminApiClient();
    const isStaff = useAppSelector((state) => state.auth.isStaff);
    const currentUserCompany = useAppSelector((state) => state.auth.company);

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return apiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    // Mutation control
    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );
    const [selectedCompany, setSelectedCompany] = useState<number>(null);
    const [userId, setUserId] = useState<number>(null);

    const companiesQuery = useQuery({
        queryKey: ["staffCompanyList"],
        queryFn: async () => {
            return await adminApiClient.adminAccountsCompaniesList();
        },
        enabled: isStaff,
    });

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.emissioneventsBulkAssignReviewerCreate({
                bulkAssignReviewerRequest: { userId: String(userId) },
                ...(assignToAllFiltered
                    ? props.filters
                    : { emissionRecordIds: props.selectedIds }),
            });
        },
        onSuccess: () => queryClient.invalidateQueries(),
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    return (
        <Modal
            visible={props.visible}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">
                Bulk assign coordinator
            </h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Select the reviewer you want to assign{" "}
                        {affectedCount ?? "..."} emission
                        {affectedCount !== 1 ? "s" : ""} to:
                    </p>
                    {isStaff && (
                        <>
                            <p className="text-sm text-gray-600 mb-1 block">
                                Company
                                <sup>(admin only)</sup>
                            </p>
                            <FilteringDropdown
                                value={selectedCompany}
                                onChange={(c) => {
                                    setSelectedCompany(c as number);
                                    setUserId(null);
                                }}
                                options={
                                    companiesQuery.data?.map((c) => ({
                                        id: c.id,
                                        displayName: c.name,
                                        searchString: c.name.toLowerCase(),
                                    })) ?? []
                                }
                            />
                            <div className="mt-3" />
                        </>
                    )}
                    <UserDropdown
                        value={userId}
                        onChange={(id) => setUserId(id)}
                        company={
                            isStaff ? selectedCompany : currentUserCompany?.id
                        }
                        hasApprovalPermission={true}
                    />
                    {props.allSelected && (
                        <div className="flex items-center gap-2 text-sm mt-4">
                            <CustomCheckbox
                                checked={assignToAllFiltered ? "yes" : "no"}
                                onClick={() =>
                                    setAssignedToAllFiltered(
                                        !assignToAllFiltered,
                                    )
                                }
                            />
                            Apply action to all filtered emissions
                        </div>
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={!userId || bulkActionMutation.isPending}
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Assign coordinator to {affectedCount ?? "..."}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Assigning reviewer...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully assigned reviewer to{" "}
                        {bulkActionMutation.data.ok} emission
                        {bulkActionMutation.data.ok !== 1 ? "s" : ""}.
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} emission
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be assigned. Download the CSV to see
                                the reason for each failure.
                            </p>
                            <BulkFailuresTruncatedNote
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={emissionFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-assign-reviewer"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

const BulkArchiveModal = (props: EmissionBulkModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return apiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.emissioneventsBulkArchiveCreate({
                emissionEventFullRequest: {} as EmissionEventFullRequest,
                ...(assignToAllFiltered
                    ? props.filters
                    : { emissionRecordIds: props.selectedIds }),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries();
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    return (
        <Modal
            visible={props.visible}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">Bulk archive</h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Are you sure you want to archive{" "}
                        {affectedCount ?? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                className="animate-spin"
                            />
                        )}{" "}
                        emission
                        {affectedCount !== 1 ? "s" : ""}?
                    </p>
                    {props.allSelected && (
                        <div className="flex items-center gap-2 text-sm mt-4">
                            <CustomCheckbox
                                checked={assignToAllFiltered ? "yes" : "no"}
                                onClick={() =>
                                    setAssignedToAllFiltered(
                                        !assignToAllFiltered,
                                    )
                                }
                            />
                            Apply action to all filtered emissions
                        </div>
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={
                                bulkActionMutation.isPending ||
                                (allSelectedCountQuery.isLoading &&
                                    props.allSelected)
                            }
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Archive{" "}
                            {affectedCount ?? (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    className="animate-spin"
                                />
                            )}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Archiving emissions...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully archived {bulkActionMutation.data.ok}{" "}
                        emission{bulkActionMutation.data.ok !== 1 ? "s" : ""}.
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} emission
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be archived. Download the CSV to see
                                the reason for each failure.
                            </p>
                            <BulkFailuresPreview
                                linkForId={emissionFailureLink}
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={emissionFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-archive"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

const BulkMoveToInProgressModal = (props: EmissionBulkModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return apiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.emissioneventsBulkMoveToInProgressCreate({
                emissionEventFullRequest: {} as EmissionEventFullRequest,
                ...(assignToAllFiltered
                    ? props.filters
                    : { emissionRecordIds: props.selectedIds }),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries();
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    return (
        <Modal
            visible={props.visible}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">
                Move to in progress
            </h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Are you sure you want to move{" "}
                        {affectedCount ?? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                className="animate-spin"
                            />
                        )}{" "}
                        emission
                        {affectedCount !== 1 ? "s" : ""} back to in progress?
                    </p>
                    {props.allSelected && (
                        <div className="flex items-center gap-2 text-sm mt-4">
                            <CustomCheckbox
                                checked={assignToAllFiltered ? "yes" : "no"}
                                onClick={() =>
                                    setAssignedToAllFiltered(
                                        !assignToAllFiltered,
                                    )
                                }
                            />
                            Apply action to all filtered emissions
                        </div>
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={
                                bulkActionMutation.isPending ||
                                (allSelectedCountQuery.isLoading &&
                                    props.allSelected)
                            }
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Move{" "}
                            {affectedCount ?? (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    className="animate-spin"
                                />
                            )}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Moving emissions to in progress...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully moved {bulkActionMutation.data.ok} emission
                        {bulkActionMutation.data.ok !== 1 ? "s" : ""} to in
                        progress.
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} emission
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be moved. Download the CSV to see the
                                reason for each failure.
                            </p>
                            <BulkFailuresPreview
                                linkForId={emissionFailureLink}
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={emissionFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-move-in-progress"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

const BulkExportModal = (props: EmissionBulkModalProps) => {
    const apiClient = useEmissionRecordsApiClient();
    const eventsApiClient = useEmissionEventsApiClient();

    const allSelectedCountQuery = useQuery({
        queryKey: ["Event", props.filters, "count"],
        queryFn: async () => {
            return eventsApiClient.emissioneventsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    const [assignToAllFiltered, setAssignedToAllFiltered] = useState(
        props.allSelected,
    );

    const affectedCount = assignToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [exportRequested, setExportRequested] = useState(false);
    const [exportId, setExportId] = useState<string>(undefined);
    const [errorMessage, setErrorMessage] = useState<string>(null);

    const onSubmit = async () => {
        setExportRequested(true);
        setErrorMessage(null);
        try {
            const response =
                await apiClient.emissionRecordsGenerateCsvReportCreate({
                    ...(assignToAllFiltered
                        ? props.filters
                        : { ids: props.selectedIds }),
                });
            if (response.status === "no_items") {
                setErrorMessage(
                    "No emission record data found for selected filters.",
                );
            } else if (response.status === "already_in_progress") {
                setErrorMessage(
                    "You already requested a data export for the given filters.",
                );
            } else {
                setExportId(response.dataDownloadId);
            }
        } catch {
            setErrorMessage("Error while generating report. Please try again.");
        }
        setExportRequested(false);
    };

    return (
        <>
            <Modal
                visible={props.visible && !exportId}
                onClose={exportRequested ? undefined : props.onClose}
                size="sm"
            >
                <h2 className="text-lg font-semibold mb-4 mt-2">
                    Export as CSV
                </h2>
                {!exportRequested && (
                    <>
                        <p className="text-sm text-gray-600 mb-3">
                            Export{" "}
                            {affectedCount ?? (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    className="animate-spin"
                                />
                            )}{" "}
                            emission
                            {affectedCount !== 1 ? "s" : ""} as CSV?
                        </p>
                        {props.allSelected && (
                            <div className="flex items-center gap-2 text-sm mt-4">
                                <CustomCheckbox
                                    checked={assignToAllFiltered ? "yes" : "no"}
                                    onClick={() =>
                                        setAssignedToAllFiltered(
                                            !assignToAllFiltered,
                                        )
                                    }
                                />
                                Apply action to all filtered emissions
                            </div>
                        )}
                        {errorMessage && (
                            <p className="text-sm text-red-600 mt-3">
                                <span className="font-bold">Error:</span>{" "}
                                {errorMessage}
                            </p>
                        )}
                        <div className="flex gap-2 mt-5">
                            <PrimaryButton
                                type="button"
                                onClick={onSubmit}
                                disabled={
                                    allSelectedCountQuery.isLoading &&
                                    props.allSelected
                                }
                            >
                                Export{" "}
                                {affectedCount ?? (
                                    <FontAwesomeIcon
                                        icon={faSpinner}
                                        className="animate-spin"
                                    />
                                )}{" "}
                                emission
                                {affectedCount !== 1 ? "s" : ""}
                            </PrimaryButton>
                            <SecondaryButton
                                type="button"
                                onClick={props.onClose}
                            >
                                Cancel
                            </SecondaryButton>
                        </div>
                    </>
                )}
                {exportRequested && (
                    <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                        <FontAwesomeIcon
                            icon={faSpinner}
                            className="animate-spin h-6"
                        />
                        Requesting export...
                    </div>
                )}
            </Modal>
            <FullscreenModal
                visible={!!exportId}
                onClose={() => {
                    setExportId(undefined);
                    props.onClose();
                }}
                title="Export Emission Data as CSV"
            >
                <DataExportStatus
                    exportId={exportId}
                    onClose={() => {
                        setExportId(undefined);
                        props.onClose();
                    }}
                />
            </FullscreenModal>
        </>
    );
};

export const BulkActions = (props: EmissionBulkProps) => {
    const isStaff = useAppSelector((state) => state.auth.isStaff);
    const canEdit = useAppSelector(
        (state) => state.auth.permissions.emissionEdit,
    );
    const canApprove = useAppSelector(
        (state) => state.auth.permissions.emissionEventApprove,
    );
    const canArchive = useAppSelector(
        (state) => state.auth.permissions.emissionEventArchive,
    );
    const canExport = useAppSelector(
        (state) => state.auth.permissions.emissionExport,
    );
    const [showBulkModal, setShowBulkModal] = useState<
        "ASSIGN" | "ASSIGN_REVIEWER" | "ARCHIVE" | "IN_PROGRESS" | "EXPORT"
    >(null);

    const canAssign = canEdit || isStaff;
    const canAssignReviewer = canApprove || isStaff;
    const canArchiveAction = canArchive || isStaff;
    const canMoveInProgress = canEdit || isStaff;
    const canExportAction = canExport || isStaff;

    const hasAnyAction =
        canAssign ||
        canAssignReviewer ||
        canArchiveAction ||
        canMoveInProgress ||
        canExportAction;
    const bulkActionsDisabled = props.selectedIds.length === 0 || !hasAnyAction;

    return (
        <>
            <BulkActionsMenu disabled={bulkActionsDisabled}>
                <BulkMenuItem
                    icon={faCircleUser}
                    label="Assign to user"
                    enabled={canAssign}
                    disabledTitle="You don't have permission to edit emission events."
                    onClick={() => setShowBulkModal("ASSIGN")}
                />
                {canAssignReviewer && (
                    <BulkMenuItem
                        icon={faUserCheck}
                        label="Assign coordinator"
                        enabled={canAssignReviewer}
                        disabledTitle="You don't have permission to assign coordinators."
                        onClick={() => setShowBulkModal("ASSIGN_REVIEWER")}
                    />
                )}
                <BulkMenuItem
                    icon={faArchive}
                    label="Archive events"
                    enabled={canArchiveAction}
                    disabledTitle="You don't have permission to archive emission events."
                    onClick={() => setShowBulkModal("ARCHIVE")}
                />
                <BulkMenuItem
                    icon={faArrowTurnLeft}
                    label="Back to in progress"
                    enabled={canMoveInProgress}
                    disabledTitle="You don't have permission to edit emission events."
                    onClick={() => setShowBulkModal("IN_PROGRESS")}
                />
                <BulkMenuItem
                    icon={faDownload}
                    label="Export as CSV"
                    enabled={canExportAction}
                    disabledTitle="You don't have permission to export emission data."
                    onClick={() => setShowBulkModal("EXPORT")}
                />
            </BulkActionsMenu>
            {showBulkModal === "ASSIGN" && (
                <BulkAssignModal
                    {...props}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "ASSIGN"}
                />
            )}
            {showBulkModal === "ASSIGN_REVIEWER" && (
                <BulkAssignReviewerModal
                    {...props}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "ASSIGN_REVIEWER"}
                />
            )}
            {showBulkModal === "ARCHIVE" && (
                <BulkArchiveModal
                    {...props}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "ARCHIVE"}
                />
            )}
            {showBulkModal === "IN_PROGRESS" && (
                <BulkMoveToInProgressModal
                    {...props}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "IN_PROGRESS"}
                />
            )}
            {showBulkModal === "EXPORT" && (
                <BulkExportModal
                    {...props}
                    onClose={() => setShowBulkModal(null)}
                    visible={showBulkModal === "EXPORT"}
                />
            )}
        </>
    );
};
